import { useTranslation } from '../presentation/common/useTranslation.js';
import { presentationText } from '../presentation/text';

import { useState } from 'react';
import { 
  Sparkles, 
  ShieldCheck, 
  Award, 
  Compass, 
  AlertTriangle, 
  Flame, 
  Skull,
  Lock
} from 'lucide-react';
import { KarmaIcon } from './RewardIcons';
import { DEFAULT_KARMA_RULES, formatKarma } from '../utils/karmaRules';
import './KarmaBarometer.css';

export function getKarmaTier(score, isBanned, suspendedUntil, rules = DEFAULT_KARMA_RULES) {
  if (isBanned) {
    return {
      key: 'banned',
      labelKey: 'karma.tier_banned',
      descKey: 'karma.tier_banned_desc',
      color: 'text-red-500',
      bgColor: 'bg-red-500',
      borderColor: 'border-red-600',
      badgeBg: 'bg-red-950/40 text-red-400 border-red-800',
      icon: Skull,
      isDanger: true,
    };
  }
  if (score <= rules.temporarySuspensionThreshold) {
    if (suspendedUntil && new Date(suspendedUntil).getTime() <= Date.now()) {
      return { key: 'released', labelKey: 'karma.tier_released', descKey: 'karma.tier_released_desc', color: 'text-red-400', bgColor: 'bg-red-500', borderColor: 'border-red-600', badgeBg: 'bg-red-950/40 text-red-400 border-red-800', icon: AlertTriangle, isDanger: true };
    }
    return { key: 'restricted', labelKey: 'karma.tier_restricted', descKey: 'karma.tier_restricted_desc', color: 'text-red-400', bgColor: 'bg-red-500', borderColor: 'border-red-600', badgeBg: 'bg-red-950/40 text-red-400 border-red-800', icon: AlertTriangle, isDanger: true };
  }
  if (score <= rules.dailyXpPenaltyThreshold) {
    return {
      key: 'outlaw',
      labelKey: 'karma.tier_outlaw',
      descKey: 'karma.tier_outlaw_desc',
      color: 'text-rose-500',
      bgColor: 'bg-rose-500',
      borderColor: 'border-rose-600',
      badgeBg: 'bg-rose-950/40 text-rose-400 border-rose-800',
      icon: Flame,
      isDanger: true,
    };
  }
  if (score <= rules.partnerRestrictionThreshold) {
    return {
      key: 'warned',
      labelKey: 'karma.tier_warned',
      descKey: 'karma.tier_warned_desc',
      color: 'text-amber-400',
      bgColor: 'bg-amber-400',
      borderColor: 'border-amber-500',
      badgeBg: 'bg-amber-950/40 text-amber-300 border-amber-800',
      icon: AlertTriangle,
      isDanger: false,
    };
  }
  if (score <= 0) {
    return {
      key: 'neutral',
      labelKey: 'karma.tier_neutral',
      descKey: 'karma.tier_neutral_desc',
      color: 'text-zinc-400',
      bgColor: 'bg-zinc-400',
      borderColor: 'border-zinc-600',
      badgeBg: 'bg-zinc-900 text-zinc-300 border-zinc-700',
      icon: Compass,
      isDanger: false,
    };
  }
  if (score < rules.honorableThreshold) {
    return {
      key: 'trusty',
      labelKey: 'karma.tier_trusty',
      descKey: 'karma.tier_trusty_desc',
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-400',
      borderColor: 'border-indigo-500',
      badgeBg: 'bg-indigo-950/40 text-indigo-300 border-indigo-800',
      icon: Award,
      isDanger: false,
    };
  }
  if (score < rules.saintThreshold) {
    return {
      key: 'honorable',
      labelKey: 'karma.tier_honorable',
      descKey: 'karma.tier_honorable_desc',
      color: 'text-sky-400',
      bgColor: 'bg-sky-400',
      borderColor: 'border-sky-500',
      badgeBg: 'bg-sky-950/40 text-sky-300 border-sky-800',
      icon: ShieldCheck,
      isDanger: false,
    };
  }
  if (score < rules.veryTrustedThreshold) return {
    key: 'trusted',
    labelKey: 'karma.tier_trusted',
    descKey: 'karma.tier_trusted_desc',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400',
    borderColor: 'border-emerald-500',
    badgeBg: 'bg-emerald-950/40 text-emerald-300 border-emerald-800',
    icon: ShieldCheck,
    isDanger: false,
  };
  if (score < rules.exemplaryThreshold) return {
    key: 'very-trusted',
    labelKey: 'karma.tier_very_trusted',
    descKey: 'karma.tier_very_trusted_desc',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400',
    borderColor: 'border-emerald-500',
    badgeBg: 'bg-emerald-950/40 text-emerald-300 border-emerald-800',
    icon: ShieldCheck,
    isDanger: false,
  };
  return {
    key: 'exemplary',
    labelKey: 'karma.tier_exemplary',
    descKey: 'karma.tier_exemplary_desc',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400',
    borderColor: 'border-emerald-500',
    badgeBg: 'bg-emerald-950/40 text-emerald-300 border-emerald-800',
    icon: Sparkles,
    isDanger: false,
  };
}

export default function KarmaBarometer({ score = 0, isBanned = false, suspendedUntil, guildFoundingMinimumKarma = 10, tribunalApplicationMinimumKarma = 10, tribunalApplicationMinimumRank = 'C', karmaRules, dailyXpPenaltyPercent = 1, guide = false }) {
  const { t } = useTranslation();
  const [selectedThreshold, setSelectedThreshold] = useState(null);
  const rules = { ...DEFAULT_KARMA_RULES, ...karmaRules };
  const tier = getKarmaTier(score, isBanned, suspendedUntil, rules);
  const Icon = tier.icon;

  const span = rules.maximum - rules.minimum;
  const position = (value) => ((rules.maximum - Math.max(rules.minimum, Math.min(rules.maximum, value))) / span) * 100;
  const pinPercentage = position(score);
  const spectrum = `linear-gradient(to right, #0f6b52 0% ${position(rules.exemplaryThreshold)}%, #137f62 ${position(rules.exemplaryThreshold)}% ${position(rules.veryTrustedThreshold)}%, #159d76 ${position(rules.veryTrustedThreshold)}% ${position(rules.saintThreshold)}%, #4c9a9c ${position(rules.saintThreshold)}% ${position(rules.honorableThreshold)}%, #738e94 ${position(rules.honorableThreshold)}% ${position(0)}%, #a69b78 ${position(0)}% ${position(rules.partnerRestrictionThreshold)}%, #c77d32 ${position(rules.partnerRestrictionThreshold)}% ${position(rules.dailyXpPenaltyThreshold)}%, #ad4551 ${position(rules.dailyXpPenaltyThreshold)}% ${position(rules.temporarySuspensionThreshold)}%, #6b1e2b ${position(rules.temporarySuspensionThreshold)}% 100%)`;
  const detailValues = { partner: formatKarma(rules.partnerRestrictionThreshold), xp: formatKarma(rules.dailyXpPenaltyThreshold), xpEnd: formatKarma(rules.temporarySuspensionThreshold + 1), tribunalLoss: formatKarma(rules.tribunalRevocationThreshold), suspension: formatKarma(rules.temporarySuspensionThreshold), minimum: formatKarma(rules.minimum), maximum: formatKarma(rules.maximum), percent: dailyXpPenaltyPercent, months: rules.temporarySuspensionMonths };
  const markers = [
    { score: rules.exemplaryThreshold, label: t('karma.marker_exemplary'), detail: t('karma.marker_exemplary_detail'), color: 'text-emerald-300', line: 'bg-emerald-300' },
    { score: rules.veryTrustedThreshold, label: t('karma.marker_very_trusted'), detail: t('karma.marker_very_trusted_detail'), color: 'text-emerald-300', line: 'bg-emerald-300' },
    { score: rules.saintThreshold, label: t('karma.marker_saint'), detail: t('karma.marker_saint_detail'), color: 'text-emerald-300', line: 'bg-emerald-300' },
    { score: rules.honorableThreshold, label: t('karma.marker_honorable'), detail: t('karma.marker_honorable_detail'), color: 'text-sky-300', line: 'bg-sky-300' },
    { score: guildFoundingMinimumKarma, label: t('karma.marker_guild'), detail: t('karma.marker_guild_detail'), color: 'text-amber-300', line: 'bg-amber-300' },
    { score: tribunalApplicationMinimumKarma, label: t('karma.marker_tribunal'), detail: t('karma.marker_tribunal_detail', { rank: tribunalApplicationMinimumRank }), color: 'text-violet-300', line: 'bg-violet-300' },
    { score: 0, label: t('karma.marker_neutral'), detail: t('karma.marker_neutral_detail'), color: 'text-zinc-300', line: 'bg-zinc-300' },
    { score: rules.partnerRestrictionThreshold, label: t('karma.marker_partner'), detail: t('karma.marker_partner_detail', detailValues), color: 'text-orange-300', line: 'bg-orange-300' },
    { score: rules.dailyXpPenaltyThreshold, label: t('karma.marker_xp'), detail: t('karma.marker_xp_detail', detailValues), color: 'text-rose-300', line: 'bg-rose-300' },
    { score: rules.tribunalRevocationThreshold, label: t('karma.marker_tribunal_loss'), detail: t('karma.marker_tribunal_loss_detail', detailValues), color: 'text-red-300', line: 'bg-red-300' },
    { score: rules.temporarySuspensionThreshold, label: t('karma.marker_suspension'), detail: t('karma.marker_suspension_detail', detailValues), color: 'text-red-400', line: 'bg-red-400' },
  ].sort((a, b) => b.score - a.score);
  const markerGroups = markers.reduce((groups, marker) => {
    const previous = groups[groups.length - 1];
    if (previous && Math.abs(position(previous.score) - position(marker.score)) < 2.5) previous.items.push(marker);
    else groups.push({ score: marker.score, items: [marker] });
    return groups;
  }, []);
  const selectedGroup = markerGroups.find(group => group.score === selectedThreshold) ||
    markerGroups.reduce((nearest, group) => Math.abs(group.score - score) < Math.abs(nearest.score - score) ? group : nearest);

  return (
    <div id="karma-barometer" className="world-panel world-karma p-5 space-y-4 scroll-mt-24" data-karma-tier={tier.key}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KarmaIcon className={`karma-tier-icon w-5 h-5 ${tier.color}`} aria-hidden="true" />
          <span className="font-serif text-xl text-amber-100">
            {t('karma.title')}
          </span>
        </div>
        {!guide && <div className={`karma-tier-badge px-2.5 py-1 text-xs font-mono font-bold border ${tier.badgeBg}`}>
          {t('karma.points', { score: score > 0 ? `+${score}` : score })}
        </div>}
      </div>

      <p className="text-sm text-slate-300">{t('karma.rules', detailValues)}</p>
      <div className="karma-scale-section" aria-label={t('karma.markers_title')}>
        <div className="karma-scale">
          <div className="karma-scale-track" style={{ background: spectrum }} />
          {markers.map((marker, index) => <span
            key={`${marker.score}-${index}`}
            className="karma-scale-tick"
            style={{ left: `${position(marker.score)}%` }}
            aria-hidden="true"
          />)}
          {!guide && <span className="karma-scale-current" style={{ left: `${pinPercentage}%` }} title={t('karma.points', { score: formatKarma(score) })} aria-hidden="true" />}
        </div>
        <div className="karma-scale-ends"><span>{formatKarma(rules.maximum)}</span><span>{formatKarma(rules.minimum)}</span></div>
        <div className="karma-scale-legend" role="group" aria-label={t('karma.markers_title')}>
          {markerGroups.map((group, index) => {
            const accent = group.items.some(item => item.score <= rules.temporarySuspensionThreshold) ? '#d96b70' :
              group.items.some(item => item.score <= rules.dailyXpPenaltyThreshold) ? '#e38b85' :
              group.items.some(item => item.score <= rules.partnerRestrictionThreshold) ? '#e4a765' :
              group.items.some(item => item.score === guildFoundingMinimumKarma) ? '#e7c67f' : '#92c9ba';
            return <button
              key={`${group.score}-${index}`}
              type="button"
              className={`karma-scale-marker${selectedGroup === group ? ' is-selected' : ''}`}
              style={{ '--marker-accent': accent }}
              title={group.items.map(item => `${formatKarma(item.score)} ${item.label}: ${item.detail}`).join('\n')}
              aria-label={group.items.map(item => `${formatKarma(item.score)} ${item.label}: ${item.detail}`).join('; ')}
              aria-pressed={selectedGroup === group}
              onClick={() => setSelectedThreshold(group.score)}
            >
              <span className="karma-scale-marker-label">
                <b>{[...new Set(group.items.map(item => formatKarma(item.score)))].join(' / ')}</b>
                <span>{group.items.map(item => item.label).join(' · ')}</span>
              </span>
            </button>;
          })}
        </div>
        <div className="karma-scale-detail" aria-live="polite">
          {selectedGroup.items.map(marker => <div key={`${marker.score}-${marker.label}`} className="karma-scale-detail-item">
            <strong className={marker.color}>{formatKarma(marker.score)} · {marker.label}</strong>
            <span>{marker.detail}</span>
          </div>)}
        </div>
      </div>

      {/* Tier Status Description Card */}
      {!guide && <div className={`karma-status-card rounded-lg p-3.5 bg-slate-950/40 border ${tier.borderColor}/40 flex items-start gap-3`}>
        <div className={`karma-status-icon p-2 bg-zinc-900 border border-zinc-800 ${tier.color} shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="space-y-1 flex-1">
          <div className="flex items-center gap-2">
            <span className={`karma-tier-title text-xs font-bold uppercase tracking-wider ${tier.color}`}>
              {t(tier.labelKey)}
            </span>
            {isBanned && (
              <span className="karma-banned-badge px-1.5 py-0.2 text-[9px] font-bold uppercase bg-red-950 text-red-400 border border-red-800 flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" /> {t('karma.tier_banned')}
              </span>
            )}
          </div>
          <p className="karma-tier-description text-xs text-zinc-400 leading-relaxed">
            {t(tier.descKey, detailValues)}
          </p>
        </div>
      </div>}
    </div>
  );
}
